// Layer 3 (Transformation): operating-curve projections — trail, current,
// what-if / bundle future point, and ledger sentence. Pure; no React.
//
// `curveJudgement` (MS-3, paul-call.A1 #251) reads a Met pair's trail and
// current point against the site's seeded working grade-recovery curve —
// the curve itself is never drawn (optiflow-update.A7, paul-email.A4); it
// only ever surfaces as point tone and the ledger's `7 d · N of M` line.

import type { Reading } from '@/versions/v1/schema/reading';
import type { GradeRecoveryCurve } from '@/versions/v1/schema/grade-recovery-curve';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { WHAT_IF_DEFAULTS } from '@/versions/v1/schema/scenario';
import { whatIfDerived } from '@/versions/v1/modules/ingestion/ProcessGraph.transform';
import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import { READINGS } from '@/versions/v1/shared/bunker-hill-readings';
import { WORKING_CURVE_BY_METAL } from '@/versions/v1/shared/bunker-hill-grade-recovery-curves';
import { judgeAgainstCurve, type CurveJudgement } from '@/versions/v1/shared/grade-recovery-curve.derive';
import { ORE_ZONES, oreZoneAt } from '@/versions/v1/shared/bunker-hill-ore-zones';
import type { OreZoneMarker } from '@/versions/v1/schema/ore-zone';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import {
  diffMinutes,
  formatClock,
  formatDayLabel,
  productionDayOf,
  shiftOf,
} from '@/versions/v1/shared/clock.derive';
import { formatValue } from '@/versions/v1/shared/reading.derive';
import { SCENARIOS, isWhatIfDefault } from '@/versions/v1/shared/ScenarioEngine';
import {
  SERIES_BY_READING_ID,
  readingsAt,
  seriesAt,
  valueAt,
} from '@/versions/v1/shared/series.derive';

export const THROUGHPUT_FRONTIER_PROVENANCE =
  '[syn] §8.5 what-if calibration (ProcessGraph.transform whatIfDerived)';

const FUTURE_DIFF_FRACTION = 0.0025;

export type OperatingCurvePair = {
  x: string;
  y: string;
};

export type OperatingCurvePoint = {
  x: number;
  y: number;
  label?: string;
  /** Production-day (or shift) start this point was computed over — carried
   * only so the curve judgement can name the day; not rendered directly. */
  at?: SeriesTime;
  /** Ore zone feeding the plant on this production day — shape channel only. */
  zone?: { id: string; label: string; marker: OreZoneMarker };
  /** Per-point tone override from the curve judgement (Q4) — when absent
   * the viz primitive falls back to the curve's overall `tone`. */
  tone?: VizTone;
};

export type OperatingCurveZoneRow = {
  zoneId: string;
  label: string;
  marker: OreZoneMarker;
  dayCount: number;
  meanDeltaGradePct: number;
  tooltip: string;
};

export type OperatingCurveSentence = {
  primary: string;
  future?: string;
};

export type OperatingCurveFutureRowPlaceholder = {
  label: string;
  tooltip: string;
};

/** Q4: the working-curve judgement for a Met pair's 7-day trail — undefined
 * for the grind (throughput) pair, which has no grade-recovery curve. */
export type OperatingCurveJudgementSummary = {
  onPairCount: number;
  total: number;
  offDays: readonly { label: string; deltaGradePct: number }[];
  current: CurveJudgement;
  /** Per-zone mean grade delta against the design curve — one ledger row each. */
  zoneRows?: readonly OperatingCurveZoneRow[];
  /** Trail days outside the ore-zone schedule — must stay 0 at the demo cursor. */
  unscheduledDays: number;
};

export type OperatingCurveProjection = {
  design: { x: number; y: number };
  frontier?: readonly OperatingCurvePoint[];
  trail: readonly OperatingCurvePoint[];
  current: OperatingCurvePoint;
  future?: OperatingCurvePoint & { label: string };
  futureRowPlaceholder?: OperatingCurveFutureRowPlaceholder;
  designRowTooltip: string;
  sentence: OperatingCurveSentence;
  provenance?: string;
  tone: VizTone;
  xLabel: string;
  yLabel: string;
  xUnit: string;
  yUnit: string;
  xDomain: [number, number];
  yDomain: [number, number];
  caption?: string;
  cursorLabel: string;
  curveJudgement?: OperatingCurveJudgementSummary;
};

function reading(id: string): Reading {
  const row = READINGS.find((r) => r.id === id);
  if (!row) throw new Error(`Unknown reading ${id}`);
  return row;
}

function unperturbedState(state: SessionState): SessionState {
  return { ...state, scenarios: [], whatIf: null };
}

function decimalsForReading(reading: Reading): number {
  if (reading.decimals !== undefined) return reading.decimals;
  const stored = reading.value ?? reading.design;
  if (stored !== undefined) {
    const s = String(stored);
    const dot = s.indexOf('.');
    return dot === -1 ? 0 : s.length - dot - 1;
  }
  return 1;
}

function computeAxisDomain(values: readonly number[], designValue: number): [number, number] {
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const rawSpan = rawMax - rawMin;
  const minSpan = Math.abs(designValue) * 0.02;
  const span = Math.max(rawSpan, minSpan);
  const center = (rawMin + rawMax) / 2;
  const dataLo = center - span / 2;
  const dataHi = center + span / 2;
  return [dataLo - 0.25 * span, dataHi + 0.25 * span];
}

function buildThroughputFrontier(xDomain: [number, number]): OperatingCurvePoint[] {
  const [lo, hi] = xDomain;
  const start = Math.floor(lo);
  const end = Math.ceil(hi);
  const out: OperatingCurvePoint[] = [];
  for (let t = start; t <= end; t++) {
    out.push({ x: t, y: frontierYAtThroughput(t) });
  }
  return out;
}

function frontierYAtThroughput(x: number): number {
  const clamped = Math.max(55, Math.min(90, x));
  return whatIfDerived({ feedStph: clamped, hardness: 1 }).p80Um;
}

function meanGradeForProductionDay(
  gradeReadingId: string,
  prod: ReturnType<typeof productionDayOf>,
  state: SessionState,
): number | undefined {
  const series = SERIES_BY_READING_ID[gradeReadingId];
  if (!series) return undefined;
  const view = seriesAt(series.id, state);
  const vals: number[] = [];
  for (const pt of view.points) {
    if (pt.value === null) continue;
    if (diffMinutes(pt.at, prod.start) < 0) continue;
    if (diffMinutes(pt.at, prod.end) > 0) continue;
    if (diffMinutes(pt.at, state.cursor) > 0) continue;
    vals.push(pt.value);
  }
  if (vals.length === 0) return undefined;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function productionDayTrail(
  pair: OperatingCurvePair,
  state: SessionState,
): OperatingCurvePoint[] {
  const ySeries = SERIES_BY_READING_ID[pair.y];
  if (!ySeries) return [];
  const yView = seriesAt(ySeries.id, state);
  const daily = yView.points.filter(
    (p): p is { at: typeof p.at; value: number } => p.value !== null,
  );
  const slice = daily.slice(-7);
  const xReading = reading(pair.x);
  const yReading = reading(pair.y);

  return slice.map((pt) => {
    const prod = productionDayOf(pt.at);
    const grade =
      meanGradeForProductionDay(pair.x, prod, state) ??
      valueAt(pair.x, state) ??
      xReading.value ??
      xReading.design ??
      0;
    const label = `${formatDayLabel(pt.at)} · ${formatValue(xReading, {
      value: grade,
      withUnit: true,
    })} · ${formatValue(yReading, { value: pt.value, withUnit: true })}`;
    const zone = oreZoneAt(pt.at);
    return {
      x: grade,
      y: pt.value,
      label,
      at: pt.at,
      zone: zone ? { id: zone.id, label: zone.label, marker: zone.marker } : undefined,
    };
  });
}

function shiftTrail(pair: OperatingCurvePair, state: SessionState): OperatingCurvePoint[] {
  const ySeries = SERIES_BY_READING_ID[pair.y];
  const xSeries = SERIES_BY_READING_ID[pair.x];
  if (!ySeries || !xSeries) return [];
  const yView = seriesAt(ySeries.id, state);
  const shiftPts = yView.points.filter(
    (p): p is { at: typeof p.at; value: number } => p.value !== null,
  );
  const slice = shiftPts.slice(-7);
  const xReading = reading(pair.x);
  const yReading = reading(pair.y);
  const xView = seriesAt(xSeries.id, state);
  const feedDecimals = decimalsForReading(xReading);

  return slice.map((pt) => {
    const { start, end } = shiftOf(pt.at);
    const cursorCap = diffMinutes(state.cursor, end) < 0 ? state.cursor : end;
    const feedVals: number[] = [];
    for (const row of xView.points) {
      if (row.value === null) continue;
      if (diffMinutes(row.at, start) < 0) continue;
      if (diffMinutes(row.at, cursorCap) > 0) continue;
      feedVals.push(row.value);
    }
    const feedMean =
      feedVals.length > 0
        ? feedVals.reduce((a, b) => a + b, 0) / feedVals.length
        : (valueAt(pair.x, state) ?? xReading.value ?? xReading.design ?? 0);
    const shiftLabel = start.split('T')[1]!.slice(0, 5);
    const label = `${formatDayLabel(start)} · ${shiftLabel} · ${formatValue(xReading, {
      value: feedMean,
      withUnit: true,
      decimals: feedDecimals,
    })} · ${formatValue(yReading, { value: pt.value, withUnit: true })}`;
    return { x: feedMean, y: pt.value, label };
  });
}

function futureActive(state: SessionState, throughputMode: boolean): boolean {
  if (state.whatIf && !isWhatIfDefault(state.whatIf)) return true;
  if (throughputMode) return false;
  return state.scenarios.length > 0;
}

function futureLabel(state: SessionState): string {
  if (state.whatIf && !isWhatIfDefault(state.whatIf)) return 'what-if';
  const id = state.scenarios[0];
  const bundle = id ? SCENARIOS.find((s) => s.id === id) : undefined;
  return bundle?.label ?? 'what-if';
}

function futurePoint(
  pair: OperatingCurvePair,
  state: SessionState,
  throughputMode: boolean,
): (OperatingCurvePoint & { label: string }) | undefined {
  if (!futureActive(state, throughputMode)) return undefined;

  if (throughputMode && state.whatIf && !isWhatIfDefault(state.whatIf)) {
    const d = whatIfDerived(state.whatIf);
    return {
      x: state.whatIf.feedStph,
      y: d.p80Um,
      label: futureLabel(state),
    };
  }

  if (!throughputMode && state.whatIf && !isWhatIfDefault(state.whatIf)) {
    if (pair.y === 'rd-kpi-ag-rec') return undefined;
    const projected = readingsAt(state);
    const xRow = projected.find((r) => r.id === pair.x)!;
    const yRow = projected.find((r) => r.id === pair.y)!;
    const d = whatIfDerived(state.whatIf);
    let y = yRow.value ?? yRow.design ?? 0;
    if (pair.y === 'rd-kpi-zn-rec') y = d.znRecPct;
    else if (pair.y === 'rd-kpi-pb-rec') y = d.pbRecPct;
    const x = xRow.value ?? xRow.design ?? 0;
    return { x, y, label: futureLabel(state) };
  }

  if (state.scenarios.length > 0) {
    const projected = readingsAt(state);
    const xRow = projected.find((r) => r.id === pair.x)!;
    const yRow = projected.find((r) => r.id === pair.y)!;
    return {
      x: xRow.value ?? xRow.design ?? 0,
      y: yRow.value ?? yRow.design ?? 0,
      label: futureLabel(state),
    };
  }

  return undefined;
}

function futureDiffersFromCurrent(
  current: OperatingCurvePoint,
  future: OperatingCurvePoint,
  xDomain: [number, number],
  yDomain: [number, number],
): boolean {
  const xSpan = xDomain[1] - xDomain[0];
  const ySpan = yDomain[1] - yDomain[0];
  const xThreshold = xSpan * FUTURE_DIFF_FRACTION;
  const yThreshold = ySpan * FUTURE_DIFF_FRACTION;
  return (
    Math.abs(future.x - current.x) > xThreshold || Math.abs(future.y - current.y) > yThreshold
  );
}

function designRowTooltip(xReading: Reading, specReadingId?: string): string {
  const deckProv = xReading.provenance ?? '';
  if (!specReadingId) return deckProv;
  const specReading = reading(specReadingId);
  const specProv = specReading.provenanceByField?.spec ?? '';
  return specProv ? `${deckProv} · ${specProv}` : deckProv;
}

function buildSentence(
  xReading: Reading,
  yReading: Reading,
  current: OperatingCurvePoint,
  design: { x: number; y: number },
  future?: OperatingCurvePoint & { label: string },
): OperatingCurveSentence {
  const deltaRec = current.y - design.y;
  const deltaGrade = current.x - design.x;

  const gradePart =
    Math.abs(deltaGrade) < 0.05
      ? 'grade at design'
      : `grade ${deltaGrade >= 0 ? '+' : '−'}${formatValue(xReading, {
          value: Math.abs(deltaGrade),
          withUnit: true,
        })}`;

  let primary: string;
  if (Math.abs(deltaRec) < 0.05) {
    primary = `at design recovery · ${gradePart}`;
  } else {
    const dir = deltaRec < 0 ? 'below' : 'above';
    const recPts = Math.abs(deltaRec).toFixed(1);
    primary = `${recPts} pts ${dir} design recovery · ${gradePart}`;
  }

  let futureLine: string | undefined;
  if (future) {
    const delta = future.y - current.y;
    const sign = delta >= 0 ? '+' : '−';
    const recFormatted = formatValue(yReading, { value: future.y, withUnit: true });
    futureLine = `${future.label} → ${recFormatted} recovery (${sign}${Math.abs(delta).toFixed(1)} pts)`;
  }

  return { primary, future: futureLine };
}

function captionFor(xReading: Reading, yReading: Reading): string {
  const gDesign = xReading.design ?? xReading.value ?? 0;
  const rDesign = yReading.design ?? yReading.value ?? 0;
  return `Design ${formatValue(xReading, {
    value: gDesign,
    withUnit: true,
  })} · ${formatValue(yReading, { value: rDesign, withUnit: true })}`;
}

function sliceTrailOverlap(
  trail: readonly OperatingCurvePoint[],
  current: OperatingCurvePoint,
): OperatingCurvePoint[] {
  if (trail.length === 0) return [];
  const last = trail[trail.length - 1]!;
  if (last.x === current.x && last.y === current.y) return trail.slice(0, -1);
  return [...trail];
}

/** Judges a trail + current point against a working curve — Q4. Returns the
 * tone-annotated trail/current alongside the ledger summary; a curve is
 * required, so this is only ever called for a Met pair. */
function applyCurveJudgement(
  trail: readonly OperatingCurvePoint[],
  current: OperatingCurvePoint,
  curve: GradeRecoveryCurve,
  tone: VizTone,
): {
  trail: OperatingCurvePoint[];
  current: OperatingCurvePoint;
  curveJudgement: OperatingCurveJudgementSummary;
} {
  const judged = trail.map((pt) => ({
    pt,
    judgement: judgeAgainstCurve(curve, { recoveryPct: pt.y, gradePct: pt.x }),
  }));
  const tonedTrail = judged.map(({ pt, judgement }) => ({
    ...pt,
    // `bad` regardless of the pair's own tone (Zn's own tone is `warn`,
    // which would otherwise collide with an off-curve mark and erase the
    // distinction) — off-curve is a stronger signal than the pair's status.
    tone: judgement.withinTolerance ? tone : ('bad' as VizTone),
  }));
  const currentJudgement = judgeAgainstCurve(curve, {
    recoveryPct: current.y,
    gradePct: current.x,
  });
  const tonedCurrent: OperatingCurvePoint = {
    ...current,
    // MS-F6: same rule as the trail above — `bad` regardless of the pair's
    // own tone, so an off-curve current point (the most important mark on
    // the chart) never collides with a pair whose own tone happens to be
    // `warn` and reads as on-curve by mistake.
    tone: currentJudgement.withinTolerance ? tone : ('bad' as VizTone),
  };
  const offDays = judged
    .filter(({ judgement }) => !judgement.withinTolerance)
    .map(({ pt, judgement }) => ({
      label: pt.at ? formatDayLabel(pt.at) : (pt.label ?? '').split(' · ')[0]!,
      deltaGradePct: judgement.deltaGradePct,
    }));
  const onPairCount = judged.filter(({ judgement }) => judgement.withinTolerance).length;

  const pairSummary = `${onPairCount} of ${judged.length} on the pair`;
  const zoneById = new Map(ORE_ZONES.map((zone) => [zone.id, zone]));
  const zoneBuckets = new Map<
    string,
    { label: string; marker: OreZoneMarker; days: { label: string; deltaGradePct: number }[] }
  >();
  let unscheduledDays = 0;
  for (const { pt, judgement } of judged) {
    const zoneId = pt.zone?.id;
    if (!zoneId) {
      unscheduledDays += 1;
      continue;
    }
    const seeded = zoneById.get(zoneId);
    const bucket = zoneBuckets.get(zoneId) ?? {
      label: seeded?.label ?? pt.zone!.label,
      marker: seeded?.marker ?? pt.zone!.marker,
      days: [],
    };
    bucket.days.push({
      label: pt.at ? formatDayLabel(pt.at) : (pt.label ?? '').split(' · ')[0]!,
      deltaGradePct: judgement.deltaGradePct,
    });
    zoneBuckets.set(zoneId, bucket);
  }
  const zoneRows: OperatingCurveZoneRow[] = [...zoneBuckets.entries()].map(
    ([zoneId, bucket], index) => {
      const meanDelta =
        bucket.days.reduce((sum, day) => sum + day.deltaGradePct, 0) / bucket.days.length;
      const dayList = bucket.days.map((d) => `${d.label} (${formatDeltaGrade(d.deltaGradePct)})`).join(', ');
      const tooltip =
        index === 0
          ? `${pairSummary} — ${dayList}`
          : dayList;
      return {
        zoneId,
        label: bucket.label,
        marker: bucket.marker,
        dayCount: bucket.days.length,
        meanDeltaGradePct: meanDelta,
        tooltip,
      };
    },
  );

  return {
    trail: tonedTrail,
    current: tonedCurrent,
    curveJudgement: {
      onPairCount,
      total: judged.length,
      offDays,
      current: currentJudgement,
      zoneRows,
      unscheduledDays,
    },
  };
}

export function formatDeltaGrade(deltaGradePct: number): string {
  const sign = deltaGradePct >= 0 ? '+' : '−';
  return `${sign}${Math.abs(deltaGradePct).toFixed(1)}`;
}

export function operatingCurve(
  pair: OperatingCurvePair,
  state: SessionState,
  opts: { tone: VizTone; throughputMode?: boolean; curve?: GradeRecoveryCurve; omitFuture?: boolean },
): OperatingCurveProjection {
  const xReading = reading(pair.x);
  const yReading = reading(pair.y);
  const throughputMode = opts.throughputMode ?? false;
  const base = unperturbedState(state);
  const trailRaw = throughputMode ? shiftTrail(pair, base) : productionDayTrail(pair, base);

  const xCur = valueAt(pair.x, base) ?? xReading.value ?? xReading.design ?? 0;
  const yCur = valueAt(pair.y, base) ?? yReading.value ?? yReading.design ?? 0;
  let current: OperatingCurvePoint = {
    x: xCur,
    y: yCur,
    label: `${formatValue(xReading, { value: xCur, withUnit: true })} · ${formatValue(yReading, { value: yCur, withUnit: true })} · ${formatClock(state.cursor)}`,
  };

  let trail = sliceTrailOverlap(trailRaw, current);

  let curveJudgement: OperatingCurveJudgementSummary | undefined;
  if (opts.curve) {
    const judged = applyCurveJudgement(trail, current, opts.curve, opts.tone);
    trail = judged.trail;
    current = judged.current;
    curveJudgement = judged.curveJudgement;
  }

  const gDesign = throughputMode
    ? (xReading.design ?? WHAT_IF_DEFAULTS.feedStph)
    : (xReading.design ?? xReading.value ?? 0);
  const rDesign = throughputMode
    ? (() => {
        if (yReading.design === undefined) {
          throw new Error(`Missing design for throughput–P80 y reading ${pair.y}`);
        }
        return yReading.design;
      })()
    : (yReading.design ?? yReading.value ?? 0);

  const xs = trail.map((p) => p.x).concat([current.x, gDesign]);
  const ys = trail.map((p) => p.y).concat([current.y, rDesign]);
  let future = opts.omitFuture ? undefined : futurePoint(pair, state, throughputMode);
  if (future) {
    xs.push(future.x);
    ys.push(future.y);
  }

  const xDomain = computeAxisDomain(xs, gDesign);
  const yDomain = computeAxisDomain(ys, rDesign);

  if (future && !futureDiffersFromCurrent(current, future, xDomain, yDomain)) {
    future = undefined;
  }

  let futureRowPlaceholder: OperatingCurveFutureRowPlaceholder | undefined;
  if (
    !opts.omitFuture &&
    !throughputMode &&
    state.whatIf &&
    !isWhatIfDefault(state.whatIf) &&
    pair.y === 'rd-kpi-ag-rec' &&
    !future
  ) {
    futureRowPlaceholder = {
      label: futureLabel(state),
      tooltip: 'no what-if model for Ag recovery',
    };
  }

  const design = { x: gDesign, y: rDesign };
  const sentence = buildSentence(xReading, yReading, current, design, future);

  const frontier = throughputMode ? buildThroughputFrontier(xDomain) : undefined;

  return {
    design,
    frontier,
    trail,
    current,
    future,
    futureRowPlaceholder,
    designRowTooltip: throughputMode
      ? `Design ${formatValue(xReading, { value: gDesign, withUnit: true })} · ${formatValue(yReading, { value: rDesign, withUnit: true })}`
      : designRowTooltip(xReading, MET_SPEC_IDS[pair.x]),
    sentence,
    provenance: throughputMode ? THROUGHPUT_FRONTIER_PROVENANCE : undefined,
    tone: opts.tone,
    xLabel: xReading.label,
    yLabel: yReading.label,
    xUnit: xReading.unit,
    yUnit: yReading.unit,
    xDomain,
    yDomain,
    caption: throughputMode ? captionFor(xReading, yReading) : undefined,
    cursorLabel: formatClock(state.cursor),
    curveJudgement,
  };
}

const MET_SPEC_IDS: Record<string, string> = {
  'rd-kpi-zn-grade': 'rd-spec-zn-grade',
  'rd-kpi-pb-grade': 'rd-spec-pb-grade',
  'rd-kpi-ag-grade': 'rd-spec-ag-grade',
};

const MET_PAIRS: readonly {
  metal: 'Zn' | 'Pb' | 'Ag';
  pair: OperatingCurvePair;
  tone: VizTone;
}[] = [
  {
    metal: 'Zn',
    pair: { x: 'rd-kpi-zn-grade', y: 'rd-kpi-zn-rec' },
    tone: 'warn',
  },
  {
    metal: 'Pb',
    pair: { x: 'rd-kpi-pb-grade', y: 'rd-kpi-pb-rec' },
    tone: 'info',
  },
  {
    metal: 'Ag',
    pair: { x: 'rd-kpi-ag-grade', y: 'rd-kpi-ag-rec' },
    tone: 'ok',
  },
];

/** Every Met pair carries its working curve — the judgement is engine data,
 * present regardless of which landing renders it (Q4); `omitFuture` is the
 * only mount-level knob (Q6 — the Met mount reads the record, not the
 * what-if decision). */
export function metOperatingCurves(
  state: SessionState,
  opts: { omitFuture?: boolean } = {},
): readonly (OperatingCurveProjection & { metal: string })[] {
  return MET_PAIRS.map(({ metal, pair, tone }) => ({
    metal,
    ...operatingCurve(pair, state, {
      tone,
      curve: WORKING_CURVE_BY_METAL[metal],
      omitFuture: opts.omitFuture,
    }),
  }));
}

export function grindOperatingCurve(state: SessionState): OperatingCurveProjection {
  return operatingCurve({ x: 'rd-c-feed', y: 'rd-spec-p80' }, state, {
    tone: 'neutral',
    throughputMode: true,
  });
}

/** Sample frontier y at throughput x — for probe verification. */
export function throughputFrontierYAt(x: number): number {
  return frontierYAtThroughput(x);
}
